import { mdiMenu } from "@mdi/js";
import Icon from "@mdi/react";
import { GatsbyLinkProps, Link, navigate } from "gatsby";
import React, { Fragment, useEffect, useState } from "react";
import styled, { ThemeConsumer } from "styled-components";
import { routes } from "../routes";
import { reactiveRule } from "../theme";

const NavContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 9999;
    display: grid;
    margin: 40px 0px;

    ${reactiveRule("lg", "and-down")} {
        margin: unset;
    }
`;

const StyledNav = styled.nav<{ $expanded: boolean }>`
    background: ${({ theme }) => theme.colors.accent};
    border-bottom-left-radius: 36px;
    border-top-left-radius: 36px;
    font-family: Anton, sans-serif;
    justify-self: right;
    box-shadow: 0px 0px 5px black;

    ul {
        list-style: none;
        display: flex;
        justify-content: right;
        align-items: center;
        text-align: center;
        margin: 0;

        li {
            line-height: 1em;
            font-size: 22px;
            display: inline-block;
            margin: 10px 20px;
            a {
                color: ${({ theme }) => theme.colors.black};
                text-decoration: none;
                &.active {
                    text-decoration: underline;
                }
            }
        }
        li:nth-child(4) {
            font-size: 19px;
        }
    }

    ${reactiveRule("lg", "and-down")} {
        position: absolute;
        width: 300px;
        top: 0;
        right: 0;
        border-radius: unset;
        border-bottom-left-radius: 70px;
        transition: 0.1s linear width;
        overflow: hidden;

        ${({ $expanded: expanded }) =>
            expanded === false &&
            `
            width: 0% !important;
            border-bottom-left-radius: 0px !important;
        `}

        ul {
            margin: 1rem;
            flex-direction: column;
            border-radius: unset;
            padding-inline-start: unset;
        }
    }

    ${reactiveRule("md", "and-down")} {
        width: 100%;
    }
`;

const NavExpander = styled.div`
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.accent};
    border-bottom-left-radius: 100%;
    padding: 5px;
    padding-left: 20px;
    padding-bottom: 20px;

    ${reactiveRule("lg", "and-up")} {
        display: none;
    }
`;

function getNavigationName(name: keyof typeof routes) {
    switch (name) {
        case "home":
            return [<Fragment key="home">Home</Fragment>];
        case "mieten":
            return [<Fragment key="mieten">Mieten</Fragment>];
        case "kaufen":
            return [<Fragment key="kaufen">Kaufen</Fragment>];
        case "anbaugeraete_zubehoer":
            return [
                <Fragment key="zubehoer-1">Anbaugeräte/</Fragment>,
                <br key="zubehoer-2" />,
                <Fragment key="zubehoer-3">Zubehör</Fragment>,
            ];
        case "kontakt":
            return [<Fragment key="kontakt">Kontakt</Fragment>];
        default:
            return [];
    }
}

const navigationLinks: { name: React.ReactNode[]; url: string }[] =
    Object.entries(routes)
        .map(([name, config]) => ({
            name: getNavigationName(name as keyof typeof routes),
            ...config,
        }))
        .filter((i) => i.name.length !== 0);

export const Navigation: React.FC<{ location: Location }> = ({ location }) => {
    const [isExpanded, setExpanded] = useState(false);
    return (
        <NavContainer>
            <StyledNav
                id="navigation"
                $expanded={isExpanded}
                onClick={() => setExpanded(false)}
            >
                <ul>
                    {navigationLinks.map(({ name, url }, index) => {
                        /* Hack for `activeClassName` to work with anchor links. If the current location starts with 
                        the URL of the link, we set the `to` prop to not include the anchor and do navigation via 
                        `navigate`. */
                        const props = (location.href || "").startsWith(
                            location.origin + url,
                        )
                            ? {
                                  to: location.pathname,
                                  onClick: (
                                      e: Parameters<
                                          Required<
                                              GatsbyLinkProps<unknown>
                                          >["onClick"]
                                      >[0],
                                  ) => {
                                      e.preventDefault();
                                      navigate(url);
                                  },
                              }
                            : {
                                  to:
                                      /* The 'home' route shall be active without hash appearing in the location. */
                                      index === 0 && location.hash === ""
                                          ? "/"
                                          : url,
                              };

                        /* Force update to `activeClassName` to trigger re-render on Link component. Else it would not
                        highlight hash links correctly on page entry. */
                        const [activeClassName, setActiveClassName] =
                            useState("invalid");
                        useEffect(() => {
                            (async () => {
                                await new Promise((resolve) =>
                                    setTimeout(resolve, 50),
                                );
                                setActiveClassName("active");
                            })();
                        }, []);

                        return (
                            <li key={index}>
                                <Link
                                    activeClassName={activeClassName}
                                    {...props}
                                >
                                    {name.map((el, index) => (
                                        <span key={index}>{el}</span>
                                    ))}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </StyledNav>
            <NavExpander
                onClick={() => setExpanded((prev) => !prev)}
                role="button"
                aria-label="Navigation"
                aria-expanded={isExpanded}
                aria-controls="navigation"
            >
                <ThemeConsumer>
                    {(theme) => (
                        <Icon
                            path={mdiMenu}
                            color={theme!.colors.black}
                            size={2}
                        />
                    )}
                </ThemeConsumer>
            </NavExpander>
        </NavContainer>
    );
};
